import NotAvailablePlaceholder from 'loyalty/components/NotAvailablePlaceholder'
import React, { useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import SlideBase from './SlideBase'
import WrappedLottieAnimation from '../WrappedLottieAnimation'
import TextButton from 'LuxKit/Button/TextButton'
import SolidShareIcon from 'LuxKit/Icons/solid/SolidShareIcon'
import Group from 'components/utils/Group'
import { PERSONALITY_GRADIENTS, PERSONALITY_IMAGE_IDS, PERSONALITY_NAMES } from './TravelPersonalityStyles'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import SparklesAnimation from '../SparklesAnimation'
import { getImageUrl } from 'lib/image/imageUtils'
import Sheet from 'components/utils/Sheet'
import FormatCurrency from 'components/Common/FormatCurrency'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import TextLink from 'LuxKit/TextLink'
import WrappedLoyaltyPercentileBadge from '../WrappedLoyaltyPercentileBadge'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import InteractiveZone from '../InteractiveZone'
import useWrappedShare from '../../hooks/useWrappedShare'
import clsx from 'clsx'
import { useAppSelector } from 'hooks/reduxHooks'
import useDistanceDisplay from 'hooks/useDistanceDisplay'
import { capitalise } from 'lib/string/stringUtils'
import { useIntl } from 'react-intl'

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
`

const TravelVibeCard = styled(Group)`
  border: 1px solid ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.L};
`

const PersonalityImageCard = styled(Sheet)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(76)};
  height: ${rem(76)};
  padding: ${rem(8)};
  border-radius: ${rem(9.6)};
  border: 0.48px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4.8px 9.6px rgba(0, 0, 0, 0.25);

  &.personality-the-sun-chaser {
    background: ${PERSONALITY_GRADIENTS['the-sun-chaser']};
  }
  &.personality-the-wellness-wanderer {
    background: ${PERSONALITY_GRADIENTS['the-wellness-wanderer']};
  }
  &.personality-the-nature-roamer {
    background: ${PERSONALITY_GRADIENTS['the-nature-roamer']};
  }
  &.personality-the-bond-builder {
    background: ${PERSONALITY_GRADIENTS['the-bond-builder']};
  }
  &.personality-the-romance-storyteller {
    background: ${PERSONALITY_GRADIENTS['the-romance-storyteller']};
  }
  &.personality-the-trend-explorer {
    background: ${PERSONALITY_GRADIENTS['the-trend-explorer']};
  }
  &.personality-the-gourmet-hunter {
    background: ${PERSONALITY_GRADIENTS['the-gourmet-hunter']};
  }
  &.personality-the-elite-traveller {
    background: ${PERSONALITY_GRADIENTS['the-elite-traveller']};
  }
  &.personality-the-curious-explorer {
    background: ${PERSONALITY_GRADIENTS['the-curious-explorer']};
  }
`

const PersonalityImageContainer = styled.div`
  position: relative;
  width: ${rem(60)};
  height: ${rem(60)};
  animation: ${float} 3s ease-in-out infinite;
  /* GPU acceleration for smooth animation */
  will-change: transform;
  transform: translateZ(0);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    will-change: auto;
  }
`

const PersonalityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`

const SparklesOverlay = styled.div`
  position: absolute;
  inset: -30%;
  pointer-events: none;
  z-index: ${zIndex.min};
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(12)};
  width: 100%;
`

const StatCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  padding: ${rem(12)} ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.L};

  &.full-width {
    grid-column: 1 / -1;
  }
`

const LoyaltyRowSlot = styled(Group)`
  margin-top: ${rem(4)};
`

const LottieStyle = { width: '100%', height: '100%' }

const TopViewedOfferCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  padding: ${rem(12)};
  border: 1px solid ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${props => props.theme.borderRadius.L};
  width: 100%;
`

const InclusionsContainer = styled(Group)`
  margin-top: ${rem(4)};
`

const MAX_VISIBLE_INCLUSIONS = 3

function SummarySlide() {
  const intl = useIntl()
  // Use granular selector to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const { handleShare, isCapturing } = useWrappedShare()
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === 'summary')

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Must call all hooks before any conditional returns (React rules of hooks)
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const name = capitalise(firstName?.trim() || 'Traveller')

  // Memoize personality image
  const personalityImage = useMemo(() => {
    if (!data) return undefined
    return getImageUrl(PERSONALITY_IMAGE_IDS[data.personality.type], {
      format: 'png',
      dpr: 3,
      width: 120,
    })
  }, [data])

  const { displayDistance, unitLabel } = useDistanceDisplay(data?.stats?.totalDistanceKm ?? 0)

  if (!data) return null

  const { stats, personality, topDestinations, loyalty, topOffer } = data

  // Low data mode: user has no booking/trip data, only offer viewing data
  const isLowData = stats.citiesVisited.length === 0

  const offerUrl = topOffer?.id ? `/offer/hotel/${topOffer.id}` : undefined
  const visibleInclusions = topOffer?.inclusions?.slice(0, MAX_VISIBLE_INCLUSIONS) ?? []
  const remainingInclusionsCount = (topOffer?.inclusions?.length ?? 0) - MAX_VISIBLE_INCLUSIONS
  const hideSavings = stats.totalSavings < 100

  // Extract ternary strings as constants before JSX return
  const topOfferCaption = isTotalsOnlyMode
    ? intl.formatMessage({ id: 'yNnTQRVEDm', defaultMessage: 'Most Loved Offer', description: 'Caption for most loved offer in totals mode on the offer page' })
    : intl.formatMessage({ id: '0QoyWMr5KV', defaultMessage: 'Top Viewed Offer', description: 'Caption for the top viewed offer statistic in the year-end wrapped summary slide' })

  const shareButtonText = isCapturing
    ? intl.formatMessage({ id: 'zrLOWg6szm', defaultMessage: 'Preparing...', description: 'Button text while preparing share for sharing content with others' })
    : intl.formatMessage({ id: 'zo8TX1YW3j', defaultMessage: 'Share your Year of Escapes', description: 'Button text to share wrapped story in the year-end travel wrapped summary' })

  return (
    <SlideBase
      background={isCurrentSlide ? <WrappedLottieAnimation key="confetti-active" animationName="confetti" position="top" /> : null}
    >
      <Group direction="vertical" gap={24} verticalAlign="end" fullHeight>
        <div>
          <Group direction="vertical" gap={24}>
            <Group direction="vertical" gap={4} fullWidth>
              <Heading variant="heading4" colour="neutral-one">
                <b>{intl.formatMessage({ id: 'HrBskYEj5n', defaultMessage: '{name}, here’s Your 2025', description: 'Heading with user name and year in the year-in-review feature' }, { name })}</b>
              </Heading>
              <Heading variant="heading1alt" colour="neutral-one">
                <i>{intl.formatMessage({ id: 'HKuyWaCeqH', defaultMessage: 'Year of Escapes', description: 'Main heading for wrapped story in the year-end travel wrapped summary' })}</i>
              </Heading>
              <BodyText variant="medium" colour="neutral-three">
                {intl.formatMessage({ id: 'f6fouv0Uuk', defaultMessage: 'What an incredible year!', description: 'Body copy text celebrating the year in the year-in-review feature' })}
              </BodyText>
            </Group>
            <Group direction="vertical" gap={12}>
              <TravelVibeCard direction="horizontal" gap={12} verticalAlign="center" fullWidth>
                <PersonalityImageCard className={`personality-${personality.type}`} paletteType="default">
                  <PersonalityImageContainer>
                    <PersonalityImage src={personalityImage} alt={PERSONALITY_NAMES[personality.type]} />
                    <SparklesOverlay>
                      <SparklesAnimation style={LottieStyle} />
                    </SparklesOverlay>
                  </PersonalityImageContainer>
                </PersonalityImageCard>
                <Group direction="vertical" gap={2} grow style={{ minWidth: 0 }}>
                  <Caption variant="medium" colour="neutral-four">{intl.formatMessage({ id: 'q1dTuXiwLQ', defaultMessage: 'Your travel style was', description: 'Descriptive caption for travel personality in the year-in-review feature' })}</Caption>
                  <Heading variant="heading4" colour="neutral-one">
                    {PERSONALITY_NAMES[personality.type]}
                  </Heading>
                </Group>
              </TravelVibeCard>

              {isLowData && (
                <StatCard>
                  <Caption variant="small" format="uppercase" colour="neutral-four">
                    {intl.formatMessage({ id: 'w9BuNWLOb9', defaultMessage: 'Viewed offers', description: 'Caption for viewed offers stat on the offer listing or detail page' })}
                  </Caption>
                  <BodyText variant="large" weight="bold" colour="neutral-one">
                    {intl.formatMessage({ id: 'fMfHQgdIiW', defaultMessage: '{count} offers', description: 'Pluralised count of offers viewed displayed in the year-end wrapped summary stats' }, { count: stats.offersViewed })}
                  </BodyText>
                </StatCard>
              )}

              {!!(isLowData && topOffer?.imageId?.length) && (
                <TopViewedOfferCard>
                  <Caption variant="medium" colour="neutral-four">
                    {topOfferCaption}
                  </Caption>
                  <BodyText variant="large" weight="bold" colour="neutral-one">
                    {topOffer.propertyName}
                  </BodyText>
                  {visibleInclusions.length > 0 && (
                    <InclusionsContainer direction="vertical" gap={4}>
                      {visibleInclusions.map((inclusion) => (
                        <OfferInclusionItem
                          key={inclusion.id}
                          inclusion={inclusion}
                          size="small"
                          lineClamp={1}
                        />
                      ))}
                      {remainingInclusionsCount > 0 && (
                        <BodyText variant="small" weight="bold" colour="neutral-one">
                          {intl.formatMessage({ id: 'Nz+WGtoxyW', defaultMessage: 'and more', description: 'Text indicating more items available in the year-in-review feature' })}
                        </BodyText>
                      )}
                    </InclusionsContainer>
                  )}
                  {offerUrl && (
                    <InteractiveZone>
                      <TextLink to={offerUrl} target="_blank" size="caption" colour="neutral-one">
                        {intl.formatMessage({ id: 'vBGYzVsxAi', defaultMessage: 'View this offer', description: 'Link text to view offer details on the offer listing or detail page' })}
                      </TextLink>
                    </InteractiveZone>
                  )}
                </TopViewedOfferCard>
              )}

              {!isLowData && (
                <StatsGrid>
                  <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      {intl.formatMessage({ id: 'sgO4ToPwig', defaultMessage: 'Destinations explored', description: 'Descriptive caption for destinations explored stat' })}
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      {intl.formatMessage(
                        {
                          id: 'decZ1Q7X9s',
                          defaultMessage: '{destinations} in {countries}',
                          description: 'Summary of destinations explored in a number of countries',
                        },
                        {
                          destinations: intl.formatMessage(
                            {
                              id: 'KrZ8wke76A',
                              defaultMessage: '{citiesCount, plural, one {# destination} other {# destinations}}',
                              description: 'Number of destinations visited in summary slide in the year-in-review feature',
                            },
                            { citiesCount: stats.citiesVisited.length },
                          ),
                          countries: intl.formatMessage(
                            {
                              id: '8N/xh95lRP',
                              defaultMessage: '{countriesCount, plural, one {# country} other {# countries}}',
                              description: 'Number of countries visited in summary slide in the wrapped summary',
                            },
                            { countriesCount: stats.countriesVisited },
                          ),
                        },
                      )}
                    </BodyText>
                  </StatCard>

                  <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      {intl.formatMessage({ id: 'bFMESzlYhs', defaultMessage: 'Nights away', description: 'Caption for the total nights away statistic in the year-end wrapped summary' })}
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      {intl.formatMessage({ id: 'E3h9HMIDvQ', defaultMessage: '{count, plural, one {# night} other {# nights}}', description: 'Pluralised count of nights displayed in the booking duration or stay summary' }, { count: stats.totalNights })}
                    </BodyText>
                  </StatCard>

                  <StatCard className={clsx({
                    'full-width': hideSavings,
                  })}
                  >
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      {intl.formatMessage({ id: '84jWMF2up/', defaultMessage: 'Distance travelled', description: 'Descriptive caption for distance travelled stat in the year-in-review feature' })}
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      <FormatDecimal value={displayDistance} maximumFractionDigits={0} /> {unitLabel}
                    </BodyText>
                  </StatCard>

                  {!hideSavings && <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      {intl.formatMessage({ id: 'pNKgY2Euv8', defaultMessage: 'Savings unlocked', description: 'Descriptive caption for savings unlocked stat in the year-in-review feature' })}
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      <FormatCurrency value={stats.totalSavings} currency={stats.currency}></FormatCurrency>
                    </BodyText>
                  </StatCard>}

                  <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      {intl.formatMessage({ id: 'SfF28rgqx/', defaultMessage: 'Top destination', description: 'Descriptive caption for top destination stat in the year-in-review feature' })}
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      {topDestinations[0]?.name ?? <NotAvailablePlaceholder variant="single" />}
                    </BodyText>
                  </StatCard>

                  <StatCard>
                    <Caption variant="small" format="uppercase" colour="neutral-four">
                      {intl.formatMessage({ id: 'w9BuNWLOb9', defaultMessage: 'Viewed offers', description: 'Caption for viewed offers stat on the offer listing or detail page' })}
                    </Caption>
                    <BodyText variant="large" weight="bold" colour="neutral-one">
                      {intl.formatMessage({ id: 'fMfHQgdIiW', defaultMessage: '{count} offers', description: 'Pluralised count of offers viewed displayed in the year-end wrapped summary stats' }, { count: stats.offersViewed })}
                    </BodyText>
                  </StatCard>

                  {!!(loyalty?.points || loyalty?.statusCredits) && (
                    <StatCard className="full-width">
                      <Caption variant="small" format="uppercase" colour="neutral-four">
                        {intl.formatMessage({ id: 'yy6li6Ehuq', defaultMessage: 'Loyalty rewards', description: 'Caption for loyalty rewards stat in the loyalty program section' })}
                      </Caption>
                      <BodyText variant="large" weight="bold" colour="neutral-one">
                        {loyalty.points > 0 && loyalty.statusCredits > 0 && intl.formatMessage(
                          {
                            id: 'Eu+T/8saly',
                            defaultMessage: '{points} Points & {statusCredits} Status Credits',
                            description: 'Loyalty rewards showing both points and status credits earned',
                          },
                          { points: loyalty.points.toLocaleString(), statusCredits: loyalty.statusCredits },
                        )}
                        {loyalty.points > 0 && !loyalty.statusCredits && intl.formatMessage(
                          {
                            id: 'DsqUwJMQzt',
                            defaultMessage: '{points} Points',
                            description: 'Loyalty rewards showing points earned in the loyalty program section',
                          },
                          { points: loyalty.points.toLocaleString() },
                        )}
                        {!loyalty.points && loyalty.statusCredits > 0 && intl.formatMessage(
                          {
                            id: 'maL85YSge7',
                            defaultMessage: '{statusCredits} Status Credits',
                            description: 'Loyalty rewards showing status credits earned in the year-in-review feature',
                          },
                          { statusCredits: loyalty.statusCredits },
                        )}
                      </BodyText>
                      <LoyaltyRowSlot direction="horizontal" gap={4} verticalAlign="center">
                        <WrappedLoyaltyPercentileBadge percentile={loyalty.percentile} tier={loyalty.tier} />
                      </LoyaltyRowSlot>
                    </StatCard>
                  )}
                </StatsGrid>
              )}
            </Group>
          </Group>
        </div>
        <InteractiveZone>
          <TextButton
            kind="primary"
            shape="round"
            onClick={handleShare}
            startIcon={<SolidShareIcon />}
            fit="full-width"
            disabled={isCapturing}
          >
            {shareButtonText}
          </TextButton>
        </InteractiveZone>
      </Group>
    </SlideBase>
  )
}

export default React.memo(SummarySlide)
